/*
 * cart-upsell.js
 * In-cart upsell logic for HPV + STD Kombi-Test bundle.
 * - Detects cart state (HPV-only, STD-only, both, bundle, none).
 * - Renders the missing-product card or the savings banner into all `[data-cart-upsell]` slots.
 * - On CTA click: posts /cart/add.js, applies BUNDLE99 discount, refreshes cart.
 *
 * Bound to:
 *   - DOMContentLoaded (initial render)
 *   - cart:updated (theme.js publishes this)
 *
 * No third-party deps. Vanilla.
 */

(function () {
  'use strict';

  // -------- Config + state --------

  function readConfig() {
    var el = document.getElementById('cart-upsell-config');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.warn('[cart-upsell] failed to parse config', e);
      return null;
    }
  }

  var cfg = readConfig();
  if (!cfg) return;

  var routes = (window.theme && window.theme.routes) || {};
  var cartUrl = routes.cart || '/cart.js';
  var cartAddUrl = routes.cartAdd || '/cart/add.js';

  var state = {
    rendering: false,
    bound: false,
    lastSignature: ''
  };

  // -------- Helpers --------

  function getSlots() {
    return document.querySelectorAll('[data-cart-upsell]');
  }

  function clearSlots() {
    getSlots().forEach(function (slot) { slot.innerHTML = ''; });
  }

  function fetchJson(url, opts) {
    return fetch(url, Object.assign({ credentials: 'same-origin' }, opts || {}))
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      });
  }

  function formatMoney(cents) {
    if (cents == null || isNaN(cents)) return '';
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, cfg.moneyFormat);
    }
    // Fallback EUR de-DE
    var euros = (cents / 100).toFixed(2).replace('.', ',');
    return euros + ' \u20AC';
  }

  function template(strTpl, vars) {
    return String(strTpl || '').replace(/\[\[(\w+)\]\]/g, function (_, k) {
      return vars[k] != null ? vars[k] : '';
    });
  }

  // -------- UTM capture (idempotent) --------

  function captureUtmsToCart() {
    try {
      var url = new URL(window.location.href);
      var keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
      var attrs = {};
      keys.forEach(function (k) {
        var v = url.searchParams.get(k);
        if (v) attrs['attributes[' + k + ']'] = v;
      });
      if (Object.keys(attrs).length === 0) return Promise.resolve();
      var body = Object.keys(attrs).map(function (k) {
        return encodeURIComponent(k) + '=' + encodeURIComponent(attrs[k]);
      }).join('&');
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body,
        credentials: 'same-origin'
      });
    } catch (e) {
      return Promise.resolve();
    }
  }

  // -------- Render logic --------

  function findItem(items, variantId) {
    if (!variantId) return null;
    for (var i = 0; i < items.length; i++) {
      if (items[i].variant_id === variantId) return items[i];
    }
    return null;
  }

  function cartSignature(cart) {
    return cart.items.map(function (i) { return i.variant_id + 'x' + i.quantity; }).sort().join('|');
  }

  function getTemplate(id) {
    var el = document.getElementById(id);
    return el && el.content ? el.content.cloneNode(true) : null;
  }

  function renderInto(slot, fragment) {
    slot.innerHTML = '';
    slot.appendChild(fragment);
  }

  function renderUpsellCard(target, lineItem) {
    var tplId = target === 'hpv' ? 'cart-upsell-tpl-card-add-hpv' : 'cart-upsell-tpl-card-add-std';
    // target = the missing single (to be added). Other product is the one currently in cart.
    var unitInCartCents = lineItem.original_line_price / lineItem.quantity;
    // Diff = what the user effectively pays for the missing single to reach the 99€ bundle total.
    var diffCents = cfg.bundleCents - unitInCartCents;
    if (diffCents < 0) diffCents = 0;
    // Strike anchor = regular single sale price of the MISSING product.
    var originalCents = target === 'hpv' ? cfg.hpvSaleCents : cfg.stdSaleCents;
    // Savings shown in subline = anchor minus diff (i.e. the bundle benefit on this second item).
    var savingsCents = (originalCents || 0) - diffCents;
    if (savingsCents < 0) savingsCents = 0;

    var slots = getSlots();
    slots.forEach(function (slot) {
      var frag = getTemplate(tplId);
      if (!frag) return;
      var sublineEl = frag.querySelector('[data-cart-upsell-subline]');
      var priceEl = frag.querySelector('[data-cart-upsell-price]');
      var strikeEl = frag.querySelector('[data-cart-upsell-strike]');
      if (sublineEl) sublineEl.textContent = template(cfg.labels.cardSublineTemplate, { savings: formatMoney(savingsCents) });
      if (priceEl) priceEl.textContent = formatMoney(diffCents);
      if (strikeEl) {
        if (originalCents && originalCents > diffCents) {
          strikeEl.textContent = template(cfg.labels.cardStrikeTemplate, { originalPrice: formatMoney(originalCents) });
          strikeEl.style.display = '';
        } else {
          strikeEl.textContent = '';
          strikeEl.style.display = 'none';
        }
      }
      renderInto(slot, frag);
    });
  }

  function renderBanner(cart) {
    // Both HPV and STD in cart, bundle not in cart.
    var hpvItem = findItem(cart.items, cfg.hpvVariantId);
    var stdItem = findItem(cart.items, cfg.stdVariantId);
    if (!hpvItem || !stdItem) return;
    // Use original_line_price (pre-discount): once the bundle discount is active the
    // final_line_price already nets out the saving, which would compute savings = 0
    // and suppress the banner. The "you save X" message is vs. the undiscounted total.
    var combined = (hpvItem.original_line_price / hpvItem.quantity) + (stdItem.original_line_price / stdItem.quantity);
    var savings = combined - cfg.bundleCents;
    if (savings <= 0) return; // nothing to brag about

    var slots = getSlots();
    slots.forEach(function (slot) {
      var frag = getTemplate('cart-upsell-tpl-bundle-banner');
      if (!frag) return;
      var textEl = frag.querySelector('[data-cart-upsell-banner-text]');
      if (textEl) textEl.textContent = template(cfg.labels.bannerTextTemplate, { savings: formatMoney(savings) });
      renderInto(slot, frag);
    });
  }

  function render(cart) {
    var sig = cartSignature(cart);
    if (sig === state.lastSignature) return;
    state.lastSignature = sig;

    var ids = cart.items.map(function (i) { return i.variant_id; });
    var hasHpv = ids.indexOf(cfg.hpvVariantId) !== -1;
    var hasStd = ids.indexOf(cfg.stdVariantId) !== -1;
    var hasBundle = ids.indexOf(cfg.bundleVariantId) !== -1;

    clearSlots();

    if (hasBundle) return; // already bundled, nothing to upsell
    if (cart.items.length === 0) return;

    if (hasHpv && hasStd) {
      renderBanner(cart);
      pushDataLayer('cart_upsell_shown', { variant: 'savings_banner' });
      return;
    }

    if (hasHpv) {
      var hpvItem = findItem(cart.items, cfg.hpvVariantId);
      renderUpsellCard('std', hpvItem);
      pushDataLayer('cart_upsell_shown', { variant: 'std_card' });
      return;
    }

    if (hasStd) {
      var stdItem = findItem(cart.items, cfg.stdVariantId);
      renderUpsellCard('hpv', stdItem);
      pushDataLayer('cart_upsell_shown', { variant: 'hpv_card' });
      return;
    }
  }

  // -------- Add-to-cart --------

  function addOtherSingle(target, btn) {
    // target = the missing single (HPV or STD) to add to cart.
    // No discount-code fetch — an Automatic Discount in the Shopify Admin handles the
    // -10.80 € bundle savings when both HPV + STD variants are in the cart.
    var variantId = target === 'hpv' ? cfg.hpvVariantId : cfg.stdVariantId;
    if (!variantId) return Promise.reject(new Error('no variant id'));
    if (btn) {
      btn.disabled = true;
      btn.classList.add('is-loading');
    }
    pushDataLayer('cart_upsell_clicked', { upsell_variant: variantId, target: target, mechanic: 'add_other_single' });
    return captureUtmsToCart()
      .then(function () {
        return fetchJson(cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
        });
      })
      .then(function () {
        pushDataLayer('cart_upsell_added', { upsell_variant: variantId, target: target, mechanic: 'add_other_single' });
        pushKlaviyo('cart_upsell_added', { upsell_variant: variantId, target: target, mechanic: 'add_other_single' });
        // Impulse rebuilds the cart drawer line items when 'cart:build' is dispatched
        // (see theme.js: document.addEventListener('cart:build', ... buildCart())).
        // The previous code fired 'cart:refresh' and called theme.cart.refresh(),
        // neither of which this theme handles — so the drawer never updated after add.
        document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        return loadAndRender();
      })
      .catch(function (err) {
        console.warn('[cart-upsell] add failed', err);
        pushDataLayer('cart_upsell_error', { error: String(err && err.message || err), target: target, mechanic: 'add_other_single' });
      })
      .then(function () {
        if (btn) {
          btn.disabled = false;
          btn.classList.remove('is-loading');
        }
      });
  }

  function bind() {
    if (state.bound) return;
    state.bound = true;
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('.js-cart-upsell-add, [data-cart-upsell-btn]');
      if (!btn) return;
      e.preventDefault();
      var target = btn.getAttribute('data-target');
      addOtherSingle(target, btn);
    });
  }

  // -------- Tracking --------

  function pushDataLayer(event, data) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: event }, data || {}));
    } catch (e) { /* swallow */ }
  }

  function pushKlaviyo(event, data) {
    try {
      window._learnq = window._learnq || [];
      window._learnq.push(['track', event, data || {}]);
    } catch (e) { /* swallow */ }
  }

  // -------- Init --------

  function loadAndRender() {
    if (state.rendering) return Promise.resolve();
    state.rendering = true;
    return fetchJson(cartUrl)
      .then(function (cart) { render(cart); })
      .catch(function (err) { console.warn('[cart-upsell] cart fetch failed', err); })
      .then(function () { state.rendering = false; });
  }

  function init() {
    // We only need HPV + STD variant IDs here — the bundle product is referenced for the
    // savings narrative but never added to cart by this script, so its publish state does
    // not gate the upsell.
    if (!cfg.hpvVariantId || !cfg.stdVariantId) {
      console.warn('[cart-upsell] missing HPV or STD variant ID in config; aborting.');
      return;
    }
    bind();
    loadAndRender();
    document.addEventListener('cart:updated', loadAndRender);
    document.addEventListener('cart:refresh', loadAndRender);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
